/**
 * A TENSOFT FELTÖLTŐ-TOKENJEI — a #54 2. lépése a felületen.
 *
 * A token a PontCo szerverén futó feltöltőé (`tools/tensoft-feltolto/`): azzal
 * jelentkezik, és csak feltölteni jogosít. Az API már a dev-en van
 * (`GET/POST /tensoft/agent-tokens`, `POST …/:id/revoke`, csak ADMIN).
 *
 * ⚠️ A TOKEN SZÖVEGE EGYSZER JÖN, A KIADÁS VÁLASZÁBAN — a szerver csak a
 * hash-ét tárolja. Ezért az `issued` itt az EGYETLEN példány: ha a felület
 * eldobja, mielőtt a könyvelő kimásolta, a tokent csak visszavonni és újat
 * kiadni lehet. A „mikor tűnhet el" szabályát a `TensoftConnection` őrzi.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import { ApiError, api } from '@/lib/api-client';

/** Egy token a listában — a backend `AgentTokenSummary`-ja, ISO-szövegekkel. */
export interface AgentToken {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

/** A kiadás válasza: a `token` csak ebben az egy válaszban szerepel. */
export interface IssuedAgentToken {
  id: string;
  label: string;
  token: string;
}

export type AgentTokensStatus = 'loading' | 'ready' | 'listFailed' | 'forbidden';

/** Az írási út hibája. `invalid`: a szerver mondata (pl. a megnevezés hossza). */
export type AgentTokenWriteError = null | { kind: 'invalid'; message: string } | { kind: 'failed' };

export interface AgentTokensState {
  status: AgentTokensStatus;
  tokens: AgentToken[];
  /** Írás van úton — a gombok addig nem nyomhatók. */
  busy: boolean;
  writeError: AgentTokenWriteError;
  issued: IssuedAgentToken | null;
  issue: (label: string) => void;
  revoke: (id: string) => void;
  /** A könyvelő jelezte, hogy elmentette — a token ezzel végleg eltűnik. */
  dismissIssued: () => void;
}

const LIST = '/tensoft/agent-tokens';

export function useAgentTokens(): AgentTokensState {
  const { signOut } = useAuth();
  const [status, setStatus] = useState<AgentTokensStatus>('loading');
  const [tokens, setTokens] = useState<AgentToken[]>([]);
  const [busy, setBusy] = useState(false);
  const [writeError, setWriteError] = useState<AgentTokenWriteError>(null);
  const [issued, setIssued] = useState<IssuedAgentToken | null>(null);

  // ⚠️ A ZÁR REFERENCIÁN ÜL, NEM ÁLLAPOTON — a `useNavCredentials` érvelése:
  // `StrictMode` alatt egy állapot-frissítőben indított kérés kétszer futna, és
  // itt a második egy MÁSODIK token lenne, amiről a könyvelő nem tud.
  const inFlight = useRef(false);
  /**
   * A LISTA-KÉRÉSEK SORSZÁMA. Egy kiadás és egy gyors visszavonás után két
   * háttér-újraolvasás fut; ha a régebbi ér vissza utoljára, a visszavont token
   * érvényesként állna a táblában (code review 10-05). Csak a legutolsó írhat.
   */
  const loadSeq = useRef(0);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** `true`: a hibát itt elintéztük (lejárt munkamenet), a hívónak nincs dolga. */
  const expired = useCallback(
    (caught: unknown): boolean => {
      if (caught instanceof ApiError && caught.status === 401) {
        signOut('expired');
        return true;
      }
      return false;
    },
    [signOut]
  );

  const load = useCallback(
    (quiet: boolean) => {
      if (!quiet) {
        setStatus('loading');
      }
      loadSeq.current += 1;
      const seq = loadSeq.current;
      void api<AgentToken[]>(LIST)
        .then((list) => {
          if (alive.current && seq === loadSeq.current) {
            setTokens(list);
            setStatus('ready');
          }
        })
        .catch((caught: unknown) => {
          // A CSENDES ÚJRAOLVASÁS HIBÁJA NÉMA: az írás MÁR SIKERÜLT. Egy hibasáv
          // itt azt sugallná, hogy a token nem jött létre — és a könyvelő
          // kiadna egy másodikat.
          if (!alive.current || quiet || expired(caught)) {
            return;
          }
          const forbidden = caught instanceof ApiError && caught.status === 403;
          setStatus(forbidden ? 'forbidden' : 'listFailed');
        });
    },
    [expired]
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const write = useCallback(
    <T>(request: () => Promise<T>, onDone: (result: T) => void) => {
      if (inFlight.current) {
        return;
      }
      inFlight.current = true;
      setBusy(true);
      setWriteError(null);
      void request()
        .then((result) => {
          if (alive.current) {
            onDone(result);
            load(true);
          }
        })
        .catch((caught: unknown) => {
          if (!alive.current || expired(caught)) {
            return;
          }
          // A szerver mondata magyarul jön, és pontosabb, mint amit itt
          // írhatnánk („A gép megnevezése 3–120 karakter.").
          setWriteError(
            caught instanceof ApiError && caught.status === 400 && caught.message
              ? { kind: 'invalid', message: caught.message }
              : { kind: 'failed' }
          );
        })
        .finally(() => {
          inFlight.current = false;
          if (alive.current) {
            setBusy(false);
          }
        });
    },
    [expired, load]
  );

  const issue = useCallback(
    (label: string) =>
      write(
        () =>
          api<IssuedAgentToken>(LIST, {
            method: 'POST',
            body: JSON.stringify({ label: label.trim() }),
          }),
        setIssued
      ),
    [write]
  );

  const revoke = useCallback(
    (id: string) =>
      write(
        () => api<void>(`${LIST}/${id}/revoke`, { method: 'POST' }),
        () => {
          // A frissen kiadott token visszavonásakor a doboza sem maradhat:
          // egy halott token kimásolása a legrosszabb, ami itt történhet.
          setIssued((current) => (current?.id === id ? null : current));
        }
      ),
    [write]
  );

  const dismissIssued = useCallback(() => setIssued(null), []);

  return { status, tokens, busy, writeError, issued, issue, revoke, dismissIssued };
}
